import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { History, Loader2, Mic, MicOff, MessageSquarePlus, Send, Sparkles, X } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { fetchChatConversations, fetchChatMessages, streamChat } from "../lib/api";
import { conversationIdFromFinishEvent, parseSseChunk } from "../lib/chatbase_sse";

// SSE parser + finish-frame extractor live in `lib/chatbase_sse.js` so they're unit-testable.
// See `lib/__tests__/chatbase_sse.test.js` for the frame-shape regression coverage.

// Browser Web Speech API — feature-detect once at module scope so we can render the mic
// button in a disabled state where it's not supported (Firefox, older Safari).
const SpeechRecognitionCtor =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

function useSpeechRecognition({ onFinal, onInterim }) {
  const recogRef = useRef(null);
  const [listening, setListening] = useState(false);
  const supported = Boolean(SpeechRecognitionCtor);

  const start = useCallback(() => {
    if (!supported) {
      toast.error("Voice input isn't supported in this browser — try Chrome, Edge, or Safari.");
      return;
    }
    if (recogRef.current) return; // already running
    const recog = new SpeechRecognitionCtor();
    recog.continuous = false;
    recog.interimResults = true;
    recog.lang = navigator.language || "en-IN";
    recog.onresult = (e) => {
      let interim = "";
      let final = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const r = e.results[i];
        if (r.isFinal) final += r[0].transcript;
        else interim += r[0].transcript;
      }
      if (interim && onInterim) onInterim(interim);
      if (final && onFinal) onFinal(final.trim());
    };
    recog.onerror = (e) => {
      if (e.error !== "aborted" && e.error !== "no-speech") {
        toast.error(`Voice input error: ${e.error}`);
      }
      setListening(false);
      recogRef.current = null;
    };
    recog.onend = () => {
      setListening(false);
      recogRef.current = null;
    };
    recog.start();
    recogRef.current = recog;
    setListening(true);
  }, [supported, onFinal, onInterim]);

  const stop = useCallback(() => {
    if (recogRef.current) {
      recogRef.current.stop();
      recogRef.current = null;
    }
    setListening(false);
  }, []);

  useEffect(() => () => stop(), [stop]);
  return { supported, listening, start, stop };
}

/**
 * Chat page — /app/chat
 * Left rail: past conversations from Chatbase, tagged with this member's userId.
 * Right pane: streaming chat with a text input + mic button.
 */
export function ChatPage() {
  const [conversations, setConversations] = useState([]);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [messages, setMessages] = useState([]); // {id, role: "user"|"assistant", text}
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false); // mobile-only Previous Conversations drawer
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  const refreshConversations = useCallback(async () => {
    try {
      const data = await fetchChatConversations();
      setConversations(data.data || []);
    } catch (err) {
      // 424 = provider disabled/misconfigured; render an empty state below and toast once.
      if (err.response?.status !== 424) {
        toast.error(err.response?.data?.detail || "Couldn't load your chat history.");
      }
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshConversations();
  }, [refreshConversations]);

  useEffect(() => {
    // When the user picks a conversation from the sidebar (or history drawer), hydrate
    // the thread from Chatbase. If a reply was mid-stream, abort it first — otherwise
    // its late text-delta events fire against a stale asstId (no-op) AND the underlying
    // HTTP stream keeps leaking until Chatbase closes it upstream.
    if (!selectedId) return;
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
      setSending(false);
    }
    setMessagesLoading(true);
    setMessages([]);
    fetchChatMessages(selectedId)
      .then((data) => {
        const hist = (data?.data || []).map((m) => ({
          id: m.id,
          role: m.role,
          text: (m.parts || []).filter((p) => p.type === "text").map((p) => p.text).join(""),
        }));
        setMessages(hist);
      })
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Couldn't load this conversation.");
      })
      .finally(() => setMessagesLoading(false));
  }, [selectedId]);

  useEffect(() => {
    // Auto-scroll on new tokens.
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const speech = useSpeechRecognition({
    onFinal: (transcript) => setInput((prev) => (prev ? `${prev} ${transcript}` : transcript)),
    onInterim: () => {},
  });

  function startNewChat() {
    if (abortRef.current) abortRef.current.abort();
    setSelectedId(null);
    setMessages([]);
    setInput("");
  }

  async function handleSend() {
    const text = input.trim();
    if (!text || sending) return;
    speech.stop();
    setInput("");
    const userMsg = { id: `local-${Date.now()}`, role: "user", text };
    const asstId = `local-${Date.now()}-a`;
    setMessages((m) => [...m, userMsg, { id: asstId, role: "assistant", text: "" }]);
    setSending(true);
    const controller = new AbortController();
    abortRef.current = controller;
    let localConversationId = selectedId;
    try {
      const reader = await streamChat({ message: text, conversationId: selectedId || undefined, signal: controller.signal });
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSseChunk(buffer);
        buffer = parsed.rest;
        for (const ev of parsed.events) {
          if (ev.type === "text-delta" && ev.delta != null) {
            setMessages((m) =>
              m.map((row) => (row.id === asstId ? { ...row, text: row.text + ev.delta } : row))
            );
          } else if (ev.type === "finish") {
            // Extraction lives in the pure helper so it's unit-tested against the real
            // frame shape captured from Chatbase v2 on 13-Feb-2026.
            const cid = conversationIdFromFinishEvent(ev);
            if (cid && !localConversationId) {
              localConversationId = cid;
              setSelectedId(cid);
            }
          } else if (ev.type === "error") {
            throw new Error(ev.detail || "Chat error.");
          }
        }
      }
      // Refresh sidebar so the new conversation appears.
      refreshConversations();
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error(err.message || "Chat failed. Try again.");
        setMessages((m) => m.filter((row) => row.id !== asstId));
      }
    } finally {
      setSending(false);
      abortRef.current = null;
    }
  }

  const disabled = sending || (!input.trim() && !speech.listening);

  const conversationSummary = useMemo(() => {
    if (conversationsLoading) return null;
    if (!conversations.length) return "No conversations yet — say hi to get started.";
    return `${conversations.length} conversation${conversations.length === 1 ? "" : "s"} on file.`;
  }, [conversations, conversationsLoading]);

  return (
    <div className="mx-auto max-w-6xl px-3 py-4 sm:px-4 sm:py-6" data-testid="chat-page">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-2 sm:mb-4 sm:gap-3">
        <div>
          <h1 className="font-serif text-2xl text-text sm:text-3xl">
            Ask the <span className="text-gold">Gurukul</span>
          </h1>
          <p className="mt-1 hidden text-sm text-muted sm:block">
            An AI assistant trained on The Money Gurukul&apos;s curriculum. Type or tap the mic to
            speak — replies stream back in real time.
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Mobile-only "Previous Conversations" — replaces the sidebar on phones. */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setHistoryOpen(true)}
            className="sm:hidden"
            data-testid="chat-history-open"
          >
            <History className="mr-1.5 h-4 w-4" /> Previous
          </Button>
          <Button variant="ghost" size="sm" onClick={startNewChat} data-testid="chat-new">
            <MessageSquarePlus className="mr-1.5 h-4 w-4" /> New chat
          </Button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
        {/* Sidebar — desktop only. Mobile users see the same list inside the drawer below. */}
        <Card data-testid="chat-sidebar" className="hidden lg:block">
          <CardContent className="flex flex-col gap-2 p-3">
            <div className="px-1 pb-1 text-[10px] uppercase tracking-widest text-muted">History</div>
            {conversationSummary && (
              <p className="px-1 text-xs text-muted">{conversationSummary}</p>
            )}
            <ul className="flex max-h-[560px] flex-col gap-1 overflow-y-auto">
              {conversations.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedId(c.id)}
                    className={`w-full rounded-md border px-2 py-2 text-left text-xs transition ${selectedId === c.id ? "border-gold/40 bg-gold/5 text-text" : "border-transparent text-muted hover:border-border hover:bg-elevated/40"}`}
                    data-testid={`chat-history-${c.id}`}
                  >
                    <div className="line-clamp-1 font-medium">{c.title || "Untitled chat"}</div>
                    <div className="mt-0.5 text-[10px] text-muted">
                      {new Date((c.updatedAt || c.createdAt) * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      {c.status === "ended" && <span className="ml-1 opacity-60">· ended</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card data-testid="chat-thread">
          <CardContent className="flex h-[calc(100vh-220px)] min-h-[420px] flex-col p-3 sm:h-[600px] sm:p-4">
            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto pr-1">
              {messagesLoading && (
                <div className="flex items-center gap-2 text-xs text-muted">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading conversation…
                </div>
              )}
              {!messagesLoading && messages.length === 0 && (
                <div className="flex h-full flex-col items-center justify-center gap-2 px-2 text-center text-muted sm:gap-3">
                  <Sparkles className="h-6 w-6 text-gold/70 sm:h-8 sm:w-8" />
                  <div className="max-w-md text-xs leading-relaxed sm:text-sm">
                    Try:{" "}
                    <em>&ldquo;What&apos;s Lean FIRE?&rdquo;</em>
                    <span className="mx-1">·</span>
                    <em>&ldquo;Explain ULIP charges&rdquo;</em>
                    <span className="mx-1 hidden sm:inline">·</span>
                    <em className="hidden sm:inline">&ldquo;Best step-up SIP strategy for a 30-year-old?&rdquo;</em>
                  </div>
                </div>
              )}
              {messages.map((m) => (
                <MessageBubble key={m.id} message={m} />
              ))}
            </div>

            <div className="mt-3 border-t border-border pt-3">
              <div className="flex items-end gap-2">
                <textarea
                  rows={2}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={speech.listening ? "Listening… speak now" : "Ask anything about your money"}
                  className="min-h-[52px] flex-1 resize-none rounded-md border border-border bg-elevated px-3 py-2 text-sm text-text placeholder:text-muted focus:border-gold/60 focus:outline-none focus:ring-2 focus:ring-gold/40"
                  data-testid="chat-input"
                />
                <Button
                  type="button"
                  variant={speech.listening ? "default" : "outline"}
                  onClick={() => (speech.listening ? speech.stop() : speech.start())}
                  disabled={!speech.supported}
                  title={
                    speech.supported
                      ? speech.listening ? "Stop listening" : "Speak your question"
                      : "Voice input isn't supported in this browser"
                  }
                  className="h-10 w-10 shrink-0 p-0"
                  data-testid="chat-mic"
                >
                  {speech.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </Button>
                <Button
                  type="button"
                  onClick={handleSend}
                  disabled={disabled}
                  className="h-10 w-10 shrink-0 p-0"
                  data-testid="chat-send"
                >
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
              {!speech.supported && (
                <p className="mt-2 text-[11px] text-muted">
                  Voice input requires Chrome, Edge, or Safari.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mobile-only history drawer, opened from the "Previous" button in the header.
         z-index bumped above the bottom tab bar (z-40) so it dims the tabs while open. */}
      {historyOpen && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm lg:hidden"
            onClick={() => setHistoryOpen(false)}
            aria-hidden="true"
          />
          <aside
            className="fixed inset-x-0 bottom-0 z-[60] flex max-h-[75vh] flex-col rounded-t-2xl border-t border-border bg-bg shadow-2xl lg:hidden"
            role="dialog"
            aria-label="Previous conversations"
            data-testid="chat-history-drawer"
          >
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div>
                <div className="font-serif text-sm text-text">Previous Conversations</div>
                {conversationSummary && (
                  <div className="text-[11px] text-muted">{conversationSummary}</div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                aria-label="Close conversations"
                className="rounded-md p-1.5 text-muted hover:bg-elevated hover:text-text"
                data-testid="chat-history-close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <ul className="flex flex-1 flex-col gap-1 overflow-y-auto p-3">
              {conversations.length === 0 && !conversationsLoading && (
                <li className="p-4 text-center text-xs text-muted">
                  No conversations yet — start a new chat.
                </li>
              )}
              {conversations.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => { setSelectedId(c.id); setHistoryOpen(false); }}
                    className={`w-full rounded-md border px-3 py-2.5 text-left text-sm transition ${selectedId === c.id ? "border-gold/40 bg-gold/5 text-text" : "border-transparent text-muted hover:border-border hover:bg-elevated/40"}`}
                    data-testid={`chat-history-mobile-${c.id}`}
                  >
                    <div className="line-clamp-1 font-medium">{c.title || "Untitled chat"}</div>
                    <div className="mt-0.5 text-[10px] text-muted">
                      {new Date((c.updatedAt || c.createdAt) * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                      {c.status === "ended" && <span className="ml-1 opacity-60">· ended</span>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        </>
      )}
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === "user";
  return (
    <div
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
      data-testid={`chat-message-${message.role}`}
    >
      <div
        className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${isUser ? "bg-gold/15 text-text" : "border border-border bg-elevated/50 text-text"}`}
      >
        {message.text || (isUser ? "" : <span className="inline-flex items-center gap-2 text-muted"><Loader2 className="h-3 w-3 animate-spin" /> Thinking…</span>)}
      </div>
    </div>
  );
}

export default ChatPage;
